import React, { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cx from 'classnames';

import { PaymentContext } from 'contexts/PaymentContext';
import { BasePopupActionPayload, BasePopupProps } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';
import { ToIframe } from 'types/enums/IframeEvents';

import { firstToUppercase } from 'helpers/utils/firstToUppercase';
import {
  Parameter,
  useLifetimeOfferPopupAnalytics,
} from 'hooks/analytics/useLifetimeOfferPopupAnalytics';
import { usePackPrice } from 'hooks/usePackagePrice';
import { getLifetimePackage } from 'store/payment/selectors';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Timer } from 'components/shared/Timer';
import { Popup } from 'components/UI/Popup';

import { ReactComponent as CheckIcon } from './images/check.svg';
import { ReactComponent as DiscountIcon } from './images/discount.svg';
import gift from './images/gift.png';

import css from './lifetimeOfferPopup.module.sass';

const LIFETIME_OFFER_TIME = 60;

type ActionsPayload = BasePopupActionPayload<'close'>;

type Props = BasePopupProps<null, (action: ActionsPayload) => void>;

export const LifetimeOfferPopup: React.FC<Props> = ({ onAction }) => {
  const { t } = useTranslation();

  const { sendMessageToIframe } = useContext(PaymentContext);

  const lifetimePackage = useSelector(getLifetimePackage);

  const packagePrice = usePackPrice({ package: lifetimePackage });

  const {
    trackLifetimeOfferPopupClick,
    trackLifetimeOfferPopupClose,
    trackLifetimeOfferPopupShow,
  } = useLifetimeOfferPopupAnalytics();

  const handleClose = useCallback(
    (parament: string) => {
      sendMessageToIframe({
        event: ToIframe.FormSubmit,
      });

      trackLifetimeOfferPopupClose(parament);

      onAction({ type: 'close' });
    },
    [onAction, sendMessageToIframe, trackLifetimeOfferPopupClose]
  );

  const handleCloseByTimer = useCallback(
    () => handleClose(Parameter.Timer),
    [handleClose]
  );
  const handleCloseByCross = useCallback(
    () => handleClose(Parameter.Cross),
    [handleClose]
  );

  const handleClick = useCallback(() => {
    sendMessageToIframe({
      event: ToIframe.FormSubmit,
      is_adjustable_price: true,
    });
    trackLifetimeOfferPopupClick();
    trackLifetimeOfferPopupClose(Parameter.Continue);

    onAction({ type: 'close' });
  }, [
    onAction,
    sendMessageToIframe,
    trackLifetimeOfferPopupClick,
    trackLifetimeOfferPopupClose,
  ]);

  useEffect(() => {
    trackLifetimeOfferPopupShow();
  }, [trackLifetimeOfferPopupShow]);

  return (
    <>
      <Popup popupClassName={css.root} onClose={handleCloseByCross}>
        <div
          className={css.gift}
          style={{
            backgroundImage: `url(${gift})`,
          }}
        />
        <div className={css.content}>
          <p className={css.headline}>{t('app:lifetime.title')}</p>
          <p className={css.headlineText}>
            {t('app:lifetime.highestDiscount')}
          </p>
          <ul className={css.list}>
            <li className={cx(css.listItemBold, css.listItem)}>
              <CheckIcon className={css.checkIcon} />
              {t('app:lifetime.noRecurring')}
            </li>
            <li className={css.listItem}>
              <CheckIcon className={css.checkIcon} />
              {t('app:lifetime.unlimitedAccess')}
            </li>
            <li className={css.listItem}>
              <CheckIcon className={css.checkIcon} />
              {t('app:lifetime.viewLikes')}
            </li>
          </ul>
          <div className={css.priceBox}>
            <p className={css.newPrice}>
              {packagePrice?.applyCurrency(packagePrice.afterDiscount)}
            </p>
            <p className={css.oldPrice}>
              {packagePrice?.applyCurrency('99.90')}
            </p>
            <div className={css.discount}>
              <DiscountIcon />
              <p className={css.discountText}>-70%</p>
            </div>
          </div>
          <div className={css.timerBox}>
            <p className={css.timerText}>{t('app:lifetime.offerEnds')}</p>
            <Timer
              className={css.timerCount}
              to={LIFETIME_OFFER_TIME}
              handleTimerFinish={handleCloseByTimer}
            />
          </div>
          <BaseButton
            type={ButtonTypes.Accent}
            onClick={handleClick}
            className={css.btn}
          >
            {t('common:continue')}
          </BaseButton>

          <p className={css.reccuringText}>
            {t('app:lifetime.disclaimer', {
              siteName: firstToUppercase(MirrorService.siteName),
            })}
          </p>
        </div>
      </Popup>
    </>
  );
};
